import { Badge, type DataTableColumn } from "@design";
import { Eye, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ContentFilterEventItem } from "@/api";
import { Text } from "@/components/Text";
import { formatDateTime } from "@/i18n/format";
import { severityVariant, useEventLabels } from "./contentSafety";
import { RevealCell } from "./RevealedUser";

function Secondary({ text }: Readonly<{ text: string }>) {
  return (
    <Text as="span" variant="body-sm" tone="secondary" className="min-w-0 wrap-anywhere md:whitespace-nowrap">
      {text}
    </Text>
  );
}

function SeverityBadge({ severity }: Readonly<{ severity: string | null }>) {
  const labels = useEventLabels();
  if (severity === null) {
    return (
      <Text as="span" variant="caption" tone="tertiary">
        {labels.severity(null)}
      </Text>
    );
  }
  return <Badge variant={severityVariant(severity)}>{labels.severity(severity)}</Badge>;
}

function StatusBadge({ filtered }: Readonly<{ filtered: boolean }>) {
  const { t } = useTranslation("adminContentSafety");
  return filtered ? (
    <Badge variant="danger" icon={<Lock size={12} aria-hidden />}>
      {t("status.blocked")}
    </Badge>
  ) : (
    <Badge variant="info" icon={<Eye size={12} aria-hidden />}>
      {t("status.annotated")}
    </Badge>
  );
}

export function useEventColumns(
  onReveal: (event: ContentFilterEventItem) => void,
): DataTableColumn<ContentFilterEventItem>[] {
  const { t } = useTranslation("adminContentSafety");
  const labels = useEventLabels();
  return [
    {
      key: "time",
      header: t("columns.time"),
      mobile: "primary",
      render: (event) => (
        <Text as="span" variant="body-sm" className="whitespace-nowrap">
          {formatDateTime(event.createdAt)}
        </Text>
      ),
    },
    { key: "source", header: t("columns.source"), render: (event) => <Secondary text={labels.source(event.source)} /> },
    {
      key: "category",
      header: t("columns.category"),
      render: (event) => (
        <Text as="span" variant="body-sm" className="min-w-0 wrap-anywhere md:wrap-break-word">
          {labels.category(event.category)}
        </Text>
      ),
    },
    { key: "severity", header: t("columns.severity"), render: (event) => <SeverityBadge severity={event.severity} /> },
    { key: "status", header: t("columns.status"), render: (event) => <StatusBadge filtered={event.filtered} /> },
    { key: "model", header: t("columns.model"), render: (event) => <Secondary text={event.model} /> },
    {
      key: "feature",
      header: t("columns.feature"),
      render: (event) => <Secondary text={event.feature ?? t("noFeature")} />,
    },
    {
      key: "user",
      header: t("columns.user"),
      align: "right",
      render: (event) => <RevealCell event={event} onReveal={onReveal} />,
    },
  ];
}
